import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FlaskConical, Heart, Sparkles, Flame, Coins, Check } from "lucide-react";
import T from "@/components/T";

const TYPE_META = {
  hp:    { Icon: Heart,    color: "#8A0303", label: "HP Potion" },
  mana:  { Icon: Sparkles, color: "#4A7BC7", label: "Mana Vial" },
  vigor: { Icon: Flame,    color: "#B89C3A", label: "Vigor Draft" },
};

/**
 * Biology bench — craft healing/mana/vigor potions from gathered materials.
 * Each recipe consumes 1× of each listed material by name plus gold, and
 * produces a stackable consumable item that lands in the player's inventory.
 */
export default function BiologyView({ ch, setCh }) {
  const [recipes, setRecipes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.biologyRecipes().then(setRecipes).catch(() => {}); }, []);

  const invNames = new Set((ch.inventory || []).map(i => i.name));
  const invCounts = (ch.inventory || []).reduce((m, i) => { m[i.name] = (m[i.name] || 0) + 1; return m; }, {});

  const craft = async (recipe) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.biologyCraft(recipe.id);
      setCh(r.character);
      toast.success(`Brewed ${r.potion.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="biology-view">
      <div className="panel p-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-[#B44BE0]" strokeWidth={1.5} />
          <span className="font-heading text-xs tracking-[0.3em] uppercase text-[#E2DFD8]"><T>Biology Bench</T></span>
        </div>
        <p className="italic text-[10px] text-[#666] mt-1">
          Brew potions from gathered materials. Each craft requires 1× of every listed material and a fee in gold.
        </p>
      </div>
      {recipes.map(r => {
        const meta = TYPE_META[r.type];
        const Icon = meta.Icon;
        const missing = r.materials.filter(m => !invNames.has(m));
        const canGold = (ch.gold || 0) >= r.cost_gold;
        const canCraft = missing.length === 0 && canGold;
        return (
          <div
            key={r.id}
            data-testid={`recipe-${r.id}`}
            className="panel p-3 space-y-2"
          >
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4" style={{ color: meta.color }} strokeWidth={1.5} />
              <span className="font-heading text-sm text-[#E2DFD8]">{r.name}</span>
              <span className="ml-auto font-mono text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>
                {meta.label} · +{r.amount}
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {r.materials.map(m => {
                const have = invCounts[m] || 0;
                return (
                  <span
                    key={m}
                    className={`px-2 py-0.5 border font-mono text-[10px] ${
                      have > 0 ? "border-[#6E8B3D] text-[#B0D060]" : "border-[#8A0303] text-[#E33333]"
                    }`}
                  >
                    {have > 0 ? <Check className="w-2.5 h-2.5 inline mr-1" strokeWidth={2.5} /> : null}
                    {m} ({have}/1)
                  </span>
                );
              })}
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 font-mono text-[10px] text-[#E5C34A]">
                <Coins className="w-3 h-3" strokeWidth={1.5} /> {r.cost_gold}g
              </span>
              <button
                data-testid={`recipe-${r.id}-craft`}
                onClick={() => craft(r)}
                disabled={busy || !canCraft}
                className="px-3 py-1 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase disabled:opacity-30 hover:bg-[#B89C3A]/10"
                title={!canGold ? "Not enough gold" : (missing.length ? `Missing: ${missing.join(", ")}` : "Brew")}
              >
                Brew
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
